import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../services/api.service';
import { Employee, EmployeeBasicInfo, SalesOfficersRole } from './employees.model';
import { PaginatedResponse } from '../common/models/common.model';
import { buildHttpParams } from '../utils';

@Injectable({
  providedIn: 'root',
})
export class EmployeesService {
  private readonly _baseUrl: string = 'employees/';

  constructor(private _apiService: ApiService) {}

  list(query: Record<string, string> | null = null): Observable<PaginatedResponse<Employee>> {
    const params = buildHttpParams(query);
    return this._apiService.get(this._baseUrl, params);
  }

  get(id: string): Observable<any> {
    return this._apiService.get(`${this._baseUrl}${id}/`);
  }

  post(reqBody: any | FormData): Observable<any> {
    return this._apiService.post(this._baseUrl, reqBody);
  }

  delete(id?: string): Observable<any> {
    return this._apiService.delete(`${this._baseUrl}${id}/`);
  }

  update(id: string, reqBody: any | FormData): Observable<any> {
    return this._apiService.put(`${this._baseUrl}${id}/`, reqBody);
  }

  partial_update(id: string, reqBody: any): Observable<any> {
    return this._apiService.patch(`employees/${id}/`, reqBody);
  }

  lighter_list(params = {}): Observable<EmployeeBasicInfo[]> {
    const q = buildHttpParams(params);
    return this._apiService.get(`${this._baseUrl}lighter_list/`, q);
  }

  get_production_manager_list(): Observable<EmployeeBasicInfo[]> {
    return this._apiService.get(`${this._baseUrl}lighter-list/production-managers/`);
  }

  getDesignations(): Observable<any[]> {
    return this._apiService.get('designations/');
  }

  getSalesUsersList(role: SalesOfficersRole): Observable<EmployeeBasicInfo[]> {
    return this._apiService.get(`${this._baseUrl}lighter-list/${role}/`);
  }

  export(): Observable<Blob> {
    return this._apiService.getBlob(`${this._baseUrl}export/`);
  }

  exportProfile(id: string): Observable<Blob> {
    return this._apiService.getBlob(`${this._baseUrl}${id}/export-profile/`);
  }

  exportMonthlyAttendance(month: number, year: number): Observable<Blob> {
    const params = buildHttpParams({ month: month.toString(), year: year.toString() });
    return this._apiService.getBlob(`${this._baseUrl}export-monthly-attendance/`, params);
  }

  exportMonthlyAttendanceById(id: string, year: number, month: number): Observable<Blob> {
    const params = buildHttpParams({ year: year.toString(), month: month.toString() });
    return this._apiService.getBlob(`${this._baseUrl}${id}/export-monthly-attendance/`, params);
  }

  exportYearlyAttendance(id: string, year: number): Observable<Blob> {
    const params = buildHttpParams({ year: year.toString() });
    return this._apiService.getBlob(`${this._baseUrl}${id}/export-yearly-attendance/`, params);
  }

  exportVisitsSummary(filters: { date?: string; from_date?: string; to_date?: string }): Observable<Blob> {
    const params = buildHttpParams(filters as Record<string, string>);
    return this._apiService.getBlob(`${this._baseUrl}export-visits-summary/`, params);
  }

  exportRouteReport(id: string, year: number, month: number | null): Observable<Blob> {
    const query: Record<string, string> = { year: year.toString() };
    if (month !== null) query['month'] = month.toString();
    const params = buildHttpParams(query);
    return this._apiService.getBlob(`${this._baseUrl}${id}/export-route-report/`, params);
  }

  exportTaReport(id: string, year: number, month: number | null): Observable<Blob> {
    const query: Record<string, string> = { year: year.toString() };
    if (month !== null) query['month'] = month.toString();
    const params = buildHttpParams(query);
    return this._apiService.getBlob(`${this._baseUrl}${id}/export-ta-report/`, params);
  }
}
